/* eslint-disable jsx-a11y/alt-text */
import { EditorState, Modifier, SelectionState, convertFromRaw, convertToRaw } from "draft-js";

import EntityTypeIdentifiers from "./EntityTypeIdentifiers";
import { OrderedMap } from "immutable";
import translateIntoHtmlString from "./serialization/translateIntoHtmlString";
import translateIntoPlainContentState from "./serialization/translateIntoPlainContentState";

/**
 * @type {Function}
 */
const noop = () => {};

/**
 * @type {Object}
 */
const DiscoursePersistencyUtils = {
  /**
   * @return {EditorState}
   */
  emptyState: function emptyState() {
    return EditorState.createEmpty(null);
  },

  /**
   * @param  {EditorState} editorState
   * @return {String}
   */
  editorStateToHtml: function editorStateToHtml(editorState) {
    const contentState = editorState.getCurrentContent();
    return translateIntoHtmlString(contentState);
  },

  /**
   * @param  {?String} htmlString
   * @return {EditorState}
   */
  htmlToEditorState: function htmlToEditorState(htmlString) {
    if (!htmlString) {
      return EditorState.createEmpty(null);
    }

    const translatedState = translateIntoPlainContentState(htmlString);
    const withoutUglyMentions = replaceUglyMentionsWithTheirDisplayText(translatedState);
    const rawContentState = convertToRaw(withoutUglyMentions);
    const strippingBlocks = findEntitiesBlockKeys(translatedState);

    return EditorState.createWithContent(
      convertFromRaw({
        ...rawContentState,
        blocks: rawContentState.blocks.filter(block => strippingBlocks.includes(block.key) === false).map(block => {
          // eslint-disable-next-line
          const { key, ...other } = block;
          return other;
        }),
      }),
    );
  },
};

/**
 * @param   {ContentState} contentState
 * @returns {ContentBlock}
 */
function findMentionsReplacements(contentState) {
  const foundEntityRanges = [];

  contentState.getBlockMap().forEach(contentBlock => {
    let lastMentionEntity;
    let lastEntityKey;

    const filterMentionEntityFn = character => {
      const entityKey = character.getEntity();

      if (entityKey) {
        const entityAtChar = contentState.getEntity(entityKey);

        if (entityAtChar.getType() === EntityTypeIdentifiers.MENTION) {
          lastMentionEntity = entityAtChar;
          lastEntityKey = entityKey;
          return true;
        }
      }

      lastMentionEntity = null;
      lastEntityKey = null;

      return false;
    };

    contentBlock.findEntityRanges(filterMentionEntityFn, (startAt, endAt) => {
      const blockKey = contentBlock.getKey();
      const displayText = lastMentionEntity.getData().getDisplayText();
      const needsReplacement = endAt - startAt > displayText.length;

      if (needsReplacement) {
        foundEntityRanges.push({
          entityKey: lastEntityKey,
          displayText,
          selection: SelectionState.createEmpty(blockKey).merge({
            anchorOffset: startAt,
            focusOffset: endAt,
          }),
        });
      }
    });
  });

  return foundEntityRanges;
}

/**
 * @param   {ContentState} contentState
 * @returns {ContentState}
 */
function replaceUglyMentionsWithTheirDisplayText(contentState) {
  const foundEntityRanges = findMentionsReplacements(contentState);

  if (foundEntityRanges.length === 0) {
    return contentState;
  }

  let modifiedState = contentState;

  foundEntityRanges.forEach(entityAtRange => {
    modifiedState = replaceUglyMentionsWithTheirDisplayText(
      Modifier.replaceText(
        modifiedState,
        entityAtRange.selection,
        entityAtRange.displayText,
        null,
        entityAtRange.entityKey,
      ),
    );
  });

  return modifiedState;
}

/**
 * @param   {ContentState} contentState
 * @returns {[]String}
 */
function findEntitiesBlockKeys(contentState) {
  const foundMentions = [];

  contentState.getBlocksAsArray().forEach(block => {
    block.findEntityRanges(character => {
      if (character.getEntity() !== null) {
        const entity = contentState.getEntity(character.getEntity());

        if (entity.getType() === EntityTypeIdentifiers.MENTION) {
          const metadata = entity.getData().getMetadata();
          foundMentions.push(`@${metadata.slug}`);
          return true;
        }
      }

      return false;
    }, noop);
  });

  if (foundMentions.length === 0) {
    return OrderedMap();
  }

  const blocks = contentState.getBlockMap().filter(block => {
    const mentions = block.getText().match(/@([\w_-]+)/gi);

    if (Array.isArray(mentions)) {
      return foundMentions.every(mention => mentions.includes(mention));
    } else {
      return false;
    }
  });

  return blocks.map(block => block.getKey());
}

export default DiscoursePersistencyUtils;
